import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import SyncedEvents from './SyncedEvents';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import ChoreBoard from './ChoreBoard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = ['😖', '😕', '🙂', '😊'];
const HOURS_LEFT  = ['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'];
const HOURS_RIGHT = ['18:00','19:00','20:00','21:00','22:00','23:00','00:00','01:00','02:00','03:00','04:00','05:00'];

// Accumulating debounce: merges rapid patches so multi-field edits aren't dropped.
function useDebouncedSave(save, delay = 700) {
  const pending = useRef({});
  const timer = useRef(null);
  return useCallback((partial) => {
    pending.current = { ...pending.current, ...partial };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const toSend = pending.current;
      pending.current = {};
      timer.current = null;
      save(toSend);
    }, delay);
  }, [save, delay]);
}

const shift = (ymd, days) => {
  const d = new Date(`${ymd}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const humanDate = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString('sv-SE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function AmethystDailyPlanner({ day, setDay }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mysida/daily-planner?date=${day}`, { withCredentials: true });
      setData(r.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (patch) => {
    try {
      await axios.put(`${API}/mysida/daily-planner/${day}`, patch, { withCredentials: true });
    } catch (e) { /* silent */ }
  }, [day]);

  const dsave = useDebouncedSave(save, 700);

  if (loading || !data) return <div className="text-center py-16 opacity-60">Laddar…</div>;

  const patchLocal = (partial) => { setData((prev) => ({ ...prev, ...partial })); };

  const setPriority = (i, v) => {
    const p = [...(data.priorities || ['', '', ''])]; p[i] = v;
    patchLocal({ priorities: p }); dsave({ priorities: p });
  };
  const setTodo = (i, v) => {
    const t = [...data.todo]; t[i] = { ...t[i], text: v };
    patchLocal({ todo: t }); dsave({ todo: t });
  };
  const toggleTodo = (i) => {
    const t = [...data.todo]; t[i] = { ...t[i], done: !t[i].done };
    patchLocal({ todo: t }); save({ todo: t });
  };
  const setRoutine = (i, v) => {
    const r = [...(data.routines || [])]; r[i] = { ...(r[i] || {}), text: v };
    patchLocal({ routines: r }); dsave({ routines: r });
  };
  const toggleRoutine = (i) => {
    const r = [...(data.routines || [])]; r[i] = { ...(r[i] || {}), done: !r[i]?.done };
    patchLocal({ routines: r }); save({ routines: r });
  };
  const setMood = (m) => { patchLocal({ mood: m }); save({ mood: m }); };
  const setWater = (n) => { patchLocal({ water: n }); save({ water: n }); };
  const setMeal  = (k, v) => { const meals = { ...data.meals, [k]: v }; patchLocal({ meals }); dsave({ meals }); };
  const setNotes = (v) => { patchLocal({ notes: v }); dsave({ notes: v }); };
  const setWork = (i, v) => { const w = [...(data.work || ['','','','','',''])]; w[i] = v; patchLocal({ work: w }); dsave({ work: w }); };
  const setThank = (i, v) => { const th = [...data.thanks]; th[i] = v; patchLocal({ thanks: th }); dsave({ thanks: th }); };
  const setProd  = (n) => { patchLocal({ productivity: n }); save({ productivity: n }); };
  const setSlot  = (h, v) => { const sc = { ...(data.schedule || {}), [h]: v }; patchLocal({ schedule: sc }); dsave({ schedule: sc }); };

  return (
    <div className="neo-card bg-white p-4 md:p-6" data-testid="amethyst-daily">
      <SyncedEvents from={day} to={day} title="Dagens händelser från kalendern och månadsöversikten" testid="daily-synced-events" />
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="text-sm italic text-[#7D6BA0]">Date</div>
          <div className="bg-[#DED0F0] rounded-full px-3 py-1 font-mono text-xs text-[#4E4370]" data-testid="daily-date-chip">{humanDate(day)}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setDay(shift(day, -1))} className="neo-btn p-1.5 bg-white" data-testid="daily-prev-btn"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setDay(new Date().toISOString().slice(0,10))} className="neo-btn px-2 py-1 bg-[#E8DCF5] text-xs font-bold" data-testid="daily-today-btn">idag</button>
          <button onClick={() => setDay(shift(day, 1))} className="neo-btn p-1.5 bg-white" data-testid="daily-next-btn"><ChevronRight className="w-4 h-4" /></button>
          <button
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/mysida/import-freeform-to-daily/${day}`, {}, { withCredentials: true });
                toast.success(`Importerat: ${r.data.todos_imported} to-do${r.data.notes_appended_chars > 0 ? ' + notes' : ''} 💜`);
                await load();
              } catch { toast.error('Kunde inte importera'); }
            }}
            className="neo-btn px-2 py-1 bg-[#DED0F0] text-xs font-bold flex items-center gap-1"
            title="Importera to-do & notes från din Fri sida för denna dag"
            data-testid="daily-import-freeform-btn"
          >
            <Download className="w-3.5 h-3.5" /> Importera Fri sida
          </button>
        </div>
        <div className="font-['Fredoka'] text-2xl sm:text-3xl italic text-[#4E4370] flex-1 text-right">Daily Planner</div>
      </div>
      <div className="h-px bg-[#DED0F0] mb-4" />

      {/* 3-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT COL */}
        <div className="space-y-4">
          {/* Top Priorities */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1">Top Priorities</div>
            {[0,1,2].map((i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5">
                <span className="w-3.5 h-3.5 border-2 border-[#8B7BAA] rounded-full flex-shrink-0" />
                <input
                  value={data.priorities?.[i] || ''}
                  onChange={(e) => setPriority(i, e.target.value)}
                  className="flex-1 bg-[#F5EEFF] rounded-full px-3 py-1 text-sm border-0 outline-none placeholder:italic"
                  placeholder="  "
                  data-testid={`daily-priority-${i}`}
                />
              </div>
            ))}
          </section>

          {/* To-Do List */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1">To-Do List</div>
            {(data.todo || []).map((t, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => toggleTodo(i)}
                  className={`w-3.5 h-3.5 border-2 border-[#8B7BAA] rounded-full flex-shrink-0 ${t.done ? 'bg-[#8B7BAA]' : ''}`}
                  data-testid={`daily-todo-check-${i}`}
                />
                <input
                  value={t.text || ''}
                  onChange={(e) => setTodo(i, e.target.value)}
                  className={`flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-sm px-1 py-0.5 ${t.done ? 'line-through opacity-50' : ''}`}
                  data-testid={`daily-todo-${i}`}
                />
              </div>
            ))}
          </section>

          {/* Routines */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1">Routines</div>
            {[0,1,2].map((i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => toggleRoutine(i)}
                  className={`w-3.5 h-3.5 border-2 border-[#8B7BAA] rounded-full flex-shrink-0 ${data.routines?.[i]?.done ? 'bg-[#8B7BAA]' : ''}`}
                  data-testid={`daily-routine-check-${i}`}
                />
                <input
                  value={data.routines?.[i]?.text || ''}
                  onChange={(e) => setRoutine(i, e.target.value)}
                  className={`flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-sm px-1 py-0.5 ${data.routines?.[i]?.done ? 'line-through opacity-50' : ''}`}
                  data-testid={`daily-routine-${i}`}
                />
              </div>
            ))}
          </section>
        </div>

        {/* MIDDLE COL */}
        <div className="space-y-4">
          {/* Mood */}
          <section>
            <div className="flex items-baseline gap-2">
              <div className="font-['Fredoka'] italic text-lg text-[#4E4370]">Mood</div>
              <div className="flex items-center gap-1.5">
                {MOODS.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setMood(i)}
                    className={`text-2xl transition ${data.mood === i ? 'scale-125' : 'opacity-40 hover:opacity-100'}`}
                    data-testid={`daily-mood-${i}`}
                  >{m}</button>
                ))}
              </div>
            </div>
          </section>

          {/* Water Balance */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1">Water Balance</div>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5,6,7,8].map((n) => (
                <button
                  key={n}
                  onClick={() => setWater(data.water === n ? n - 1 : n)}
                  className={`w-6 h-8 rounded-b-md border-2 border-[#8B7BAA] flex items-end justify-center transition ${data.water >= n ? 'bg-[#C8B6FF]' : 'bg-white'}`}
                  data-testid={`daily-water-${n}`}
                >
                  <span className="text-[8px] mb-0.5 opacity-50">{n}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Meals */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1">Meals</div>
            {['b','l','d'].map((k) => (
              <div key={k} className="flex items-baseline gap-2 mb-1">
                <div className="italic w-4 text-[#7D6BA0]">{k.toUpperCase()}</div>
                <input
                  value={data.meals?.[k] || ''}
                  onChange={(e) => setMeal(k, e.target.value)}
                  placeholder={k === 'd' && data.week_dinner ? `${data.week_dinner} (från veckoplanen)` : ''}
                  className="flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-sm px-1 py-0.5"
                  data-testid={`daily-meal-${k}`}
                />
              </div>
            ))}
          </section>

          {/* Notes */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1">Notes</div>
            <textarea
              value={data.notes || ''}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-transparent border-b border-dotted border-[#C8B6FF] outline-none text-sm resize-none leading-relaxed"
              style={{ backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 21px, #C8B6FF 21px, #C8B6FF 22px)' }}
              data-testid="daily-notes"
            />
          </section>

          {/* Jobb-rader (Work) */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1 flex items-center gap-1">💼 Jobb</div>
            {[0,1,2,3,4,5].map((i) => (
              <input
                key={i}
                value={data.work?.[i] || ''}
                onChange={(e) => setWork(i, e.target.value)}
                placeholder={`Jobb ${i+1}`}
                className="w-full bg-transparent border-b border-[#C8B6FF] outline-none text-sm py-1"
                data-testid={`daily-work-${i}`}
              />
            ))}
          </section>

          {/* Thanks for */}
          <section>
            <div className="font-['Fredoka'] italic text-lg text-[#4E4370] mb-1">Thanks for</div>
            {[0,1,2,3,4].map((i) => (
              <div key={i} className="flex items-baseline gap-2 mb-1">
                <div className="italic w-4 text-[#7D6BA0]">{i + 1}</div>
                <input
                  value={data.thanks?.[i] || ''}
                  onChange={(e) => setThank(i, e.target.value)}
                  className="flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-sm px-1 py-0.5"
                  data-testid={`daily-thanks-${i}`}
                />
              </div>
            ))}
          </section>

          {/* Productivity */}
          <section>
            <div className="flex items-baseline gap-2">
              <div className="font-['Fredoka'] italic text-lg text-[#4E4370]">Productivity</div>
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setProd(data.productivity === n ? n - 1 : n)}
                    className="text-xl transition"
                    data-testid={`daily-productivity-${n}`}
                  >
                    {data.productivity >= n ? '★' : '☆'}
                  </button>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT COL — Schedule */}
        <div className="grid grid-cols-2 gap-2">
          {[HOURS_LEFT, HOURS_RIGHT].map((col, ci) => (
            <div key={ci} className="space-y-1">
              {col.map((h) => (
                <div key={h} className="flex items-center bg-[#F5EEFF] rounded-md pl-2">
                  <div className="italic text-xs text-[#7D6BA0] w-14 flex-shrink-0">{h}</div>
                  <input
                    value={data.schedule?.[h] || ''}
                    onChange={(e) => setSlot(h, e.target.value)}
                    className="flex-1 bg-transparent border-l border-[#C8B6FF] pl-2 py-1 text-xs outline-none"
                    data-testid={`daily-slot-${h.replace(':','')}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4"><ChoreBoard date={day} freqs={['day']} title="Dagens sysslor" /></div>
    </div>
  );
}
